import type { QueryKey } from "@tanstack/react-query";
import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { OptionCategoryHistoryEntry } from "../optionCategoryList/types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { api } from "./apiClient";
import { invalidateAll } from "./invalidation";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/option-categories`;

export type OptionCategoryType =
  | "categoriesOfPersonalData"
  | "categoriesOfDataSubjects"
  | "projects"
  | "technicalSecurityMeasurements"
  | "categoriesOfProcessings";

const keys = {
  all: (categoryType: OptionCategoryType): QueryKey => ["optionCategories", categoryType],
  list: (categoryType: OptionCategoryType): QueryKey => [...keys.all(categoryType), "gridview"],
  selectOptions: (categoryType: OptionCategoryType): QueryKey => [...keys.all(categoryType), "selectOptions"],
  history: (categoryType: OptionCategoryType): QueryKey => [...keys.all(categoryType), "history"],
} as const;

const OptionCategoryRowSchema = z.object({
  id: z.string(),
  value: z.string(),
  label: z.string(),
  canBeEdited: z.boolean(),
  isInUse: z.boolean().default(false),
  isShared: z.boolean().optional(),
  metadata: z.object({ description: z.string().optional() }).optional(),
  connectedEntities: z.array(z.object({ formType: z.string(), numberOfConnections: z.number() })),
});

export type OptionCategoryRowResponse = z.infer<typeof OptionCategoryRowSchema>;

export const optionCategoryListQueryOptions = (categoryType: OptionCategoryType) =>
  queryOptions({
    queryKey: keys.list(categoryType),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT}/${categoryType}/gridview`, {
        schema: z.object({ options: z.array(OptionCategoryRowSchema) }),
      }),
    throwOnError: true,
  });

export const optionCategoryHistoryQueryOptions = (categoryType: OptionCategoryType) =>
  queryOptions({
    queryKey: keys.history(categoryType),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT}/${categoryType}/history`, {
        schema: z.custom<{ history: Array<OptionCategoryHistoryEntry> }>(),
      }),
    throwOnError: true,
  });

const OptionCategorySelectOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  metadata: z.object({ description: z.string().optional() }).optional(),
});

export type OptionCategorySelectOption = z.infer<typeof OptionCategorySelectOptionSchema>;

export const optionCategorySelectOptionsQueryOptions = (categoryType: OptionCategoryType) =>
  queryOptions({
    queryKey: keys.selectOptions(categoryType),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT}/${categoryType}/select-options`, {
        schema: z.object({ options: z.array(OptionCategorySelectOptionSchema) }),
      }),
    throwOnError: true,
  });

export type OptionCategoryFormValues = { label: string; description: string };

export const OPTION_CATEGORY_HAS_DESCRIPTION: Record<OptionCategoryType, boolean> = {
  categoriesOfProcessings: true,
  categoriesOfPersonalData: false,
  categoriesOfDataSubjects: false,
  projects: true,
  technicalSecurityMeasurements: true,
};

type OptionCategoryVariables = { categoryType: OptionCategoryType; values: OptionCategoryFormValues };

const toOptionPayload = ({ categoryType, values: { label, description } }: OptionCategoryVariables) =>
  OPTION_CATEGORY_HAS_DESCRIPTION[categoryType]
    ? { label, metadata: { type: `${categoryType}Metadata`, description } }
    : { label };

export const createOptionCategoryMutationOptions = () =>
  mutationOptions({
    mutationFn: (variables: OptionCategoryVariables) =>
      api.post(`${BASE_ENDPOINT}/${variables.categoryType}`, toOptionPayload(variables), {
        schema: z.object({ id: z.string(), value: z.string() }),
      }),
    onSuccess: () => invalidateAll(),
  });

export const updateOptionCategoryMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, ...variables }: OptionCategoryVariables & { id: string }) =>
      api.patch(`${BASE_ENDPOINT}/${variables.categoryType}/${id}`, toOptionPayload(variables)),
    onSuccess: () => invalidateAll(),
  });

export const deleteOptionCategoryMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ categoryType, id }: { categoryType: OptionCategoryType; id: string }) =>
      api.delete(`${BASE_ENDPOINT}/${categoryType}/${id}`),
    onSettled: () => invalidateAll(),
  });
